import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Eye, Save, Loader2, RefreshCw, Info } from 'lucide-react';
import api from '../../lib/api';
import Button from '../Button';
import toast from 'react-hot-toast';
import ConfirmationDialog from '../ConfirmationDialog';
import SegmentedControl from '../SegmentedControl';
import ToggleSwitch from '@shared/ui/ToggleSwitch';
import { HelpTrigger } from '../../help';
import BrandingEditor from './BrandingEditor';
import BrandingPreview from './BrandingPreview';
import {
  BRANDING_DEFAULTS,
  type BrandingScope,
  type BrandingSummary,
  type BrandingSurface,
  type BrandingValues,
} from './brandingTypes';

interface Props {
  organizationId: string;
  organizationName: string;
  /** Reports exist as a surface only for orgs flagged into next-gen-reporting. */
  reportsEnabled?: boolean;
}

type AppliesTo = 'both' | 'separate';

const stripLayout = (values: BrandingValues): BrandingValues => ({
  ...values,
  layout_template: null,
});

/**
 * Organization branding — one brand, optionally split per surface.
 *
 * Replaces the two tabs (Outreach Branding / Report Branding) that edited two
 * tables through the same component. The flow is deliberately "author the
 * brand, then say where it applies": the editor renders first, and the
 * apply-to question sits below it. An admin who wants one identity never picks
 * a scope, and the word "scope" appears nowhere in the UI.
 */
const BrandingTab: React.FC<Props> = ({
  organizationId,
  organizationName,
  reportsEnabled = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [confirmMerge, setConfirmMerge] = useState(false);

  const [appliesTo, setAppliesTo] = useState<AppliesTo>('both');
  const [surface, setSurface] = useState<BrandingSurface>('outreach');
  /** Per-scope drafts, so switching surfaces never discards unsaved edits. */
  const [drafts, setDrafts] = useState<Record<BrandingScope, BrandingValues>>({
    shared: BRANDING_DEFAULTS,
    outreach: BRANDING_DEFAULTS,
    report: stripLayout(BRANDING_DEFAULTS),
  });
  const [inheriting, setInheriting] = useState<Record<BrandingSurface, boolean>>({
    outreach: true,
    report: true,
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/organizations/${organizationId}/branding/summary`);
      const summary: BrandingSummary | undefined = response.data?.data;
      if (!summary) return;

      const shared = summary.stored.shared ?? { ...BRANDING_DEFAULTS, use_shared: false };
      setDrafts({
        shared: { ...BRANDING_DEFAULTS, ...shared },
        /* A surface that has never diverged opens pre-filled from the shared
           brand, so "set separately" starts from the org's own work rather
           than a blank form. */
        outreach: { ...BRANDING_DEFAULTS, ...(summary.stored.outreach ?? shared) },
        report: stripLayout({ ...BRANDING_DEFAULTS, ...(summary.stored.report ?? shared) }),
      });
      setInheriting({
        outreach: summary.stored.outreach?.use_shared ?? true,
        report: summary.stored.report?.use_shared ?? true,
      });
      setAppliesTo(summary.per_surface ? 'separate' : 'both');
    } catch (error) {
      console.error('Failed to fetch branding settings:', error);
      toast.error('Could not load branding settings');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  /** The scope the editor is currently pointed at. */
  const activeScope: BrandingScope = appliesTo === 'both' ? 'shared' : surface;
  const activeValues = drafts[activeScope];
  const readOnly = appliesTo === 'separate' && inheriting[surface];

  const patch = (values: Partial<BrandingValues>) =>
    setDrafts((prev) => ({
      ...prev,
      [activeScope]: { ...prev[activeScope], ...values },
    }));

  const handleSave = async () => {
    try {
      setSaving(true);
      const scope = activeScope;
      const body: Record<string, unknown> = { ...drafts[scope] };
      if (scope === 'shared') {
        body.applies_to_both = appliesTo === 'both';
      } else {
        body.use_shared = inheriting[scope as BrandingSurface];
      }
      await api.put(`/organizations/${organizationId}/branding?scope=${scope}`, body);
      toast.success(
        scope === 'shared' ? 'Branding saved' : `${SURFACE_LABEL[scope as BrandingSurface]} branding saved`
      );
      await load();
    } catch (error: any) {
      console.error('Failed to save branding settings:', error);
      const detail = error?.response?.data?.details?.[0];
      toast.error(
        detail ||
          (activeScope === 'shared'
            ? "Couldn't save branding"
            : `Couldn't save ${SURFACE_LABEL[activeScope as BrandingSurface]} branding`)
      );
    } finally {
      setSaving(false);
    }
  };

  /** Resets the draft only — nothing is written until Save. */
  const handleReset = () => {
    const defaults = activeScope === 'report' ? stripLayout(BRANDING_DEFAULTS) : BRANDING_DEFAULTS;
    setDrafts((prev) => ({ ...prev, [activeScope]: defaults }));
    toast.success('Reset to defaults — save to apply');
  };

  const applyToBoth = () => {
    setAppliesTo('both');
    setConfirmMerge(false);
  };

  const previewSurfaces: BrandingSurface[] = useMemo(() => {
    if (appliesTo === 'separate') return [surface];
    return reportsEnabled ? ['outreach', 'report'] : ['outreach'];
  }, [appliesTo, surface, reportsEnabled]);

  const previewValues = (target: BrandingSurface): BrandingValues => {
    if (appliesTo === 'both') {
      return target === 'report' ? stripLayout(drafts.shared) : drafts.shared;
    }
    return inheriting[target]
      ? target === 'report'
        ? stripLayout(drafts.shared)
        : drafts.shared
      : drafts[target];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
              Branding
            </h3>
            <HelpTrigger contentKey="org-branding" />
          </div>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Your organization&apos;s logo, colors, and fonts
            {reportsEnabled ? ' for public outreach pages and reports' : ' for public outreach pages'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleReset} disabled={readOnly}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button variant="secondary" onClick={() => setShowPreview(!showPreview)}>
            <Eye className="w-4 h-4 mr-2" />
            {showPreview ? 'Hide' : 'Preview'}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || readOnly}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save changes
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">Branding defaults</h4>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              These are the <strong>defaults</strong> for new outreach items
              {reportsEnabled ? ' and reports' : ''}. An individual outreach can still be
              customized in the Design step of the Create Outreach wizard
              {reportsEnabled ? ', and an individual report in the report wizard' : ''}.
            </p>
          </div>
        </div>
      </div>

      <div className={showPreview ? 'grid grid-cols-1 lg:grid-cols-2 gap-6' : ''}>
        <div className="space-y-6">
          {appliesTo === 'separate' && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <SegmentedControl
                ariaLabel="Branding surface"
                value={surface}
                onChange={setSurface}
                options={[
                  { id: 'outreach', label: 'Outreach' },
                  { id: 'report', label: 'Reports' },
                ]}
              />
              <div className="flex items-center gap-2">
                <ToggleSwitch
                  checked={inheriting[surface]}
                  onChange={(next) => setInheriting((prev) => ({ ...prev, [surface]: next }))}
                  label={`Use shared branding for ${SURFACE_LABEL[surface]}`}
                />
                <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                  Use shared branding for {SURFACE_LABEL[surface]}
                </span>
              </div>
            </div>
          )}

          {readOnly && (
            <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
              {SURFACE_LABEL[surface]} follows your shared branding. Switch off{' '}
              <em>Use shared branding</em> above to set it separately.
            </p>
          )}

          <BrandingEditor
            value={readOnly ? drafts.shared : activeValues}
            onChange={patch}
            showLayoutTemplate={activeScope !== 'report'}
            readOnly={readOnly}
          />

          {reportsEnabled && (
            <div className="border border-border-light dark:border-border-dark rounded-lg p-4">
              <h4 className="font-medium text-text-primary dark:text-text-dark-primary mb-1">
                Where does this branding apply?
              </h4>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-3">
                Most organizations use one identity everywhere.
              </p>
              <SegmentedControl
                ariaLabel="Where this branding applies"
                value={appliesTo}
                onChange={(next) => {
                  if (next === 'both' && appliesTo === 'separate') {
                    setConfirmMerge(true);
                    return;
                  }
                  setAppliesTo(next);
                }}
                options={[
                  { id: 'both', label: 'Outreach pages and Reports' },
                  { id: 'separate', label: 'Set separately' },
                ]}
              />
            </div>
          )}
        </div>

        {showPreview && (
          <div className="space-y-4">
            {previewSurfaces.map((target) => (
              <BrandingPreview
                key={target}
                surface={target}
                value={previewValues(target)}
                organizationName={organizationName}
              />
            ))}
            {previewSurfaces.includes('report') && (
              <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                Report output does not apply these values yet — the settings ship ahead of the
                report rendering that will use them.
              </p>
            )}
          </div>
        )}
      </div>

      {confirmMerge && (
        <ConfirmationDialog
          isOpen
          title="Use one branding everywhere?"
          message="Outreach and Reports will both use your shared branding. Your separate settings are kept, and come back if you switch to Set separately again."
          confirmText="Use one branding"
          onConfirm={applyToBoth}
          onCancel={() => setConfirmMerge(false)}
        />
      )}
    </div>
  );
};

const SURFACE_LABEL: Record<BrandingSurface, string> = {
  outreach: 'Outreach',
  report: 'Reports',
};

export default BrandingTab;
